import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit2, Trash2, User, Search, Hash, Camera, Upload, Wand2, Copy, Check, KeyRound, ToggleLeft, ToggleRight } from 'lucide-react';

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [captureMode, setCaptureMode] = useState('upload');
  const [cameraStream, setCameraStream] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);
  const [assignResult, setAssignResult] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [formData, setFormData] = useState({
    employee_code: '', password_pin: '', name: '', email: '', phone: '',
    shift_start: '09:00', shift_end: '18:00', work_hours: 8, work_minutes: 0,
    work_location_id: '', photo_base64: '',
    salary_basic: 0, salary_da: 0, salary_hra: 0, paid_leave_balance: 12,
  });

  useEffect(() => { fetchEmployees(); fetchLocations(); }, []);

  const fetchEmployees = async () => {
    try { const r = await api.get('/employees'); setEmployees(r.data); }
    catch { toast.error('Failed to fetch employees'); }
    finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    try { const r = await api.get('/locations'); setLocations(r.data); }
    catch { console.error('Failed to fetch locations'); }
  };

  const generateCredentials = async () => {
    try {
      const r = await api.get('/employees/generate-credentials');
      setFormData(p => ({ ...p, employee_code: r.data.employee_code, password_pin: r.data.password_pin }));
      toast.success('Credentials generated!');
    } catch { toast.error('Failed to generate'); }
  };

  const assignCodesToLegacy = async () => {
    setAssigning(true);
    try {
      const r = await api.post('/employees/assign-codes');
      if (r.data.assigned > 0) {
        setAssignResult(r.data);
        toast.success(`Assigned codes to ${r.data.assigned} employees`);
        fetchEmployees();
      } else {
        toast.info('All employees already have codes assigned');
      }
    } catch { toast.error('Failed to assign codes'); }
    finally { setAssigning(false); }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      setCameraStream(stream);
    } catch { toast.error('Camera access denied'); }
  };

  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraStream]);

  const stopCamera = useCallback(() => {
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
  }, [cameraStream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const v = videoRef.current, c = canvasRef.current;
      if (!v.videoWidth) { toast.error('Camera not ready'); return; }
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext('2d').drawImage(v, 0, 0);
      setFormData(p => ({ ...p, photo_base64: c.toDataURL('image/jpeg', 0.8).split(',')[1] }));
      stopCamera(); toast.success('Photo captured!');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(p => ({ ...p, photo_base64: reader.result.split(',')[1] }));
      reader.readAsDataURL(file);
      toast.success('Photo uploaded!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(formData.employee_code)) { toast.error('Employee Code must be 6 digits'); return; }
    if (!selectedEmployee && !/^\d{6}$/.test(formData.password_pin)) { toast.error('PIN must be 6 digits'); return; }
    try {
      const payload = { ...formData };
      if (!payload.photo_base64) delete payload.photo_base64;
      if (selectedEmployee && !payload.password_pin) delete payload.password_pin;

      if (selectedEmployee) {
        await api.put(`/employees/${selectedEmployee.id}`, payload);
        toast.success('Employee updated');
      } else {
        const res = await api.post('/employees', payload);
        setLastCreated({ name: formData.name, employee_code: res.data.employee_code, password_pin: res.data.password_pin });
        toast.success('Employee created');
      }
      setDialogOpen(false); resetForm(); fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/employees/${selectedEmployee.id}`);
      toast.success('Employee deleted');
      setDeleteDialogOpen(false); setSelectedEmployee(null); fetchEmployees();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (emp) => {
    try {
      const res = await api.put(`/employees/${emp.id}/toggle-status`);
      toast.success(res.data.message);
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, is_active: res.data.is_active } : e));
    } catch { toast.error('Failed to toggle status'); }
  };

  const resetForm = () => {
    setFormData({
      employee_code: '', password_pin: '', name: '', email: '', phone: '',
      shift_start: '09:00', shift_end: '18:00', work_hours: 8, work_minutes: 0,
      work_location_id: '', photo_base64: '',
      salary_basic: 0, salary_da: 0, salary_hra: 0, paid_leave_balance: 12,
    });
    setSelectedEmployee(null); setCaptureMode('upload'); stopCamera();
  };

  const openEditDialog = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      employee_code: emp.employee_code || '', password_pin: '', name: emp.name, email: emp.email,
      phone: emp.phone || '', shift_start: emp.shift_start, shift_end: emp.shift_end,
      work_hours: emp.work_hours || 8, work_minutes: emp.work_minutes || 0,
      work_location_id: emp.work_location_id || '', photo_base64: '',
      salary_basic: emp.salary_basic || 0, salary_da: emp.salary_da || 0,
      salary_hra: emp.salary_hra || 0, paid_leave_balance: emp.paid_leave_balance ?? 12,
    });
    setDialogOpen(true);
  };

  const copyLoginDetails = (emp) => {
    const text = `Login Details:\nEmployee Code: ${emp.employee_code}\nPIN: ${emp.pin || 'N/A'}\nName: ${emp.name}`;
    navigator.clipboard.writeText(text);
    setCopiedId(emp.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Login details copied!');
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (emp.employee_code || '').includes(searchQuery)
  );

  useEffect(() => { if (!dialogOpen) stopCamera(); }, [dialogOpen, stopCamera]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Employees</h1>
          <p className="text-slate-500">Manage employee profiles, codes and salary</p>
        </div>
        <div className="flex gap-2">
          {employees.some(e => !e.employee_code || e.employee_code === 'N/A') && (
            <Button onClick={assignCodesToLegacy} disabled={assigning} variant="outline" className="border-[#FF9933] text-[#FF9933]" data-testid="assign-codes-btn">
              <KeyRound className="w-4 h-4 mr-2" /> {assigning ? 'Assigning...' : 'Assign Missing Codes'}
            </Button>
          )}
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Last Created Banner */}
      {lastCreated && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-green-800">Employee Created: {lastCreated.name}</p>
              <p className="text-sm text-green-700">Code: <span className="font-mono font-bold">{lastCreated.employee_code}</span> | PIN: <span className="font-mono font-bold">{lastCreated.password_pin}</span></p>
            </div>
            <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => {
              navigator.clipboard.writeText(`Employee: ${lastCreated.name}\nCode: ${lastCreated.employee_code}\nPIN: ${lastCreated.password_pin}`);
              toast.success('Credentials copied!');
            }} data-testid="copy-last-created">
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Assign Result Banner */}
      {assignResult && assignResult.employees?.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium text-blue-800">Assigned codes to {assignResult.assigned} employees</p>
              <Button size="sm" variant="ghost" onClick={() => setAssignResult(null)} className="text-blue-500">Dismiss</Button>
            </div>
            <div className="space-y-1">
              {assignResult.employees.map((emp) => (
                <div key={emp.employee_code} className="flex items-center justify-between text-sm bg-white p-2 rounded">
                  <span className="font-medium">{emp.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[#FF9933]">Code: {emp.employee_code}</span>
                    <span className="font-mono text-slate-600">PIN: {emp.pin}</span>
                    <Button size="sm" variant="ghost" onClick={() => {
                      navigator.clipboard.writeText(`Employee: ${emp.name}\nCode: ${emp.employee_code}\nPIN: ${emp.pin}`);
                      toast.success('Copied!');
                    }}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search by name, email, or code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="employee-search" />
      </div>

      <Card className="bg-white border shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="employee-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Employee</TableHead>
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">PIN</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Shift</TableHead>
                <TableHead className="font-semibold">Salary</TableHead>
                <TableHead className="font-semibold">Leaves</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
              ) : filteredEmployees.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No employees found</TableCell></TableRow>
              ) : (
                filteredEmployees.map((emp) => (
                  <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate(`/employee/${emp.id}`)} data-testid={`emp-link-${emp.id}`}>
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                          {emp.photo_url ? <img src={emp.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-slate-400" />}
                        </div>
                        <span className="font-medium text-[#FF9933] hover:underline">{emp.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF9933]/10 text-[#FF9933] rounded font-mono font-bold text-sm">
                        <Hash className="w-3 h-3" />{emp.employee_code || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded font-mono text-sm" data-testid={`pin-${emp.id}`}>
                        <KeyRound className="w-3 h-3" />{emp.pin || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleStatus(emp); }}
                        className="flex items-center gap-1.5 cursor-pointer"
                        data-testid={`toggle-status-${emp.id}`}
                      >
                        {emp.is_active !== false ? (
                          <><ToggleRight className="w-6 h-6 text-[#138808]" /><span className="text-xs font-medium text-[#138808]">Active</span></>
                        ) : (
                          <><ToggleLeft className="w-6 h-6 text-red-400" /><span className="text-xs font-medium text-red-500">Inactive</span></>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-slate-600">{emp.email}</TableCell>
                    <TableCell className="text-slate-600">{emp.shift_start} - {emp.shift_end}</TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {(emp.salary_basic || 0) + (emp.salary_da || 0) + (emp.salary_hra || 0) > 0
                        ? `Rs.${((emp.salary_basic || 0) + (emp.salary_da || 0) + (emp.salary_hra || 0)).toLocaleString()}`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-slate-600">{emp.paid_leave_balance ?? 12}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => copyLoginDetails(emp)} className="text-slate-500 hover:text-blue-500" data-testid={`copy-login-${emp.id}`}>
                          {copiedId === emp.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)} className="text-slate-600 hover:text-[#FF9933]" data-testid={`edit-employee-${emp.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedEmployee(emp); setDeleteDialogOpen(true); }} className="text-slate-600 hover:text-red-500" data-testid={`delete-employee-${emp.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>{selectedEmployee ? 'Update employee info' : 'Create employee with login credentials'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Credentials */}
            <div className="p-3 bg-[#FF9933]/5 border border-[#FF9933]/20 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-[#FF9933]">Login Credentials</Label>
                {!selectedEmployee && (
                  <Button type="button" size="sm" variant="outline" onClick={generateCredentials} className="text-xs" data-testid="generate-credentials-btn">
                    <Wand2 className="w-3 h-3 mr-1" /> Auto Generate
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">6-Digit Code *</Label>
                  <Input value={formData.employee_code} onChange={(e) => setFormData(p => ({ ...p, employee_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    placeholder="e.g. 100001" maxLength={6} required className="font-mono font-bold tracking-widest text-center" data-testid="employee-code-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">6-Digit PIN {selectedEmployee ? '(leave blank to keep)' : '*'}</Label>
                  <Input value={formData.password_pin} onChange={(e) => setFormData(p => ({ ...p, password_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    placeholder={selectedEmployee ? 'Keep current' : 'e.g. 432156'} maxLength={6} required={!selectedEmployee}
                    className="font-mono font-bold tracking-widest text-center" data-testid="employee-pin-input" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Full Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required data-testid="employee-name-input" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required data-testid="employee-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} data-testid="employee-phone-input" /></div>
              <div className="space-y-2">
                <Label>Work Location</Label>
                <Select value={formData.work_location_id} onValueChange={(v) => setFormData(p => ({ ...p, work_location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Shift Start</Label><Input type="time" value={formData.shift_start} onChange={(e) => setFormData(p => ({ ...p, shift_start: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Shift End</Label><Input type="time" value={formData.shift_end} onChange={(e) => setFormData(p => ({ ...p, shift_end: e.target.value }))} /></div>
            </div>

            {/* Salary */}
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
              <Label className="font-semibold text-slate-700">Monthly Salary (Rs.)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Basic</Label>
                  <Input type="number" value={formData.salary_basic} onChange={(e) => setFormData(p => ({ ...p, salary_basic: parseFloat(e.target.value) || 0 }))} data-testid="salary-basic" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">DA</Label>
                  <Input type="number" value={formData.salary_da} onChange={(e) => setFormData(p => ({ ...p, salary_da: parseFloat(e.target.value) || 0 }))} data-testid="salary-da" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">HRA</Label>
                  <Input type="number" value={formData.salary_hra} onChange={(e) => setFormData(p => ({ ...p, salary_hra: parseFloat(e.target.value) || 0 }))} data-testid="salary-hra" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">Gross = Basic + DA + HRA = Rs. {((formData.salary_basic || 0) + (formData.salary_da || 0) + (formData.salary_hra || 0)).toLocaleString()}</p>
            </div>

            {/* Paid Leave Balance */}
            <div className="space-y-2">
              <Label>Paid Leave Balance (days/year)</Label>
              <Input type="number" value={formData.paid_leave_balance} onChange={(e) => setFormData(p => ({ ...p, paid_leave_balance: parseInt(e.target.value) || 0 }))} data-testid="paid-leave-balance" />
            </div>

            {/* Photo */}
            <div className="space-y-3">
              <Label>Employee Photo</Label>
              <Tabs value={captureMode} onValueChange={(v) => { setCaptureMode(v); if (v === 'camera') setTimeout(startCamera, 100); else stopCamera(); }}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-1" /> Upload</TabsTrigger>
                  <TabsTrigger value="camera"><Camera className="w-4 h-4 mr-1" /> Camera</TabsTrigger>
                </TabsList>
                <TabsContent value="upload" className="mt-3">
                  <div className="flex items-center gap-4">
                    <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="photo-upload" />
                    <label htmlFor="photo-upload" className="flex-1 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#FF9933] text-center">
                      <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" /><p className="text-sm text-slate-600">Click to upload</p>
                    </label>
                    {(formData.photo_base64 || selectedEmployee?.photo_url) && (
                      <div className="w-20 h-20 rounded-lg overflow-hidden border"><img src={formData.photo_base64 ? `data:image/jpeg;base64,${formData.photo_base64}` : selectedEmployee?.photo_url} alt="" className="w-full h-full object-cover" /></div>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="camera" className="mt-3">
                  {!cameraStream && !formData.photo_base64 && (
                    <div className="flex flex-col items-center p-6 bg-slate-100 rounded-lg">
                      <Camera className="w-10 h-10 text-slate-400 mb-3" />
                      <Button type="button" onClick={startCamera} variant="outline" data-testid="start-camera-btn"><Camera className="w-4 h-4 mr-1" />Start Camera</Button>
                    </div>
                  )}
                  {cameraStream && (
                    <>
                      <div className="relative rounded-lg overflow-hidden border-2 bg-slate-900 aspect-[4/3]">
                        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover absolute inset-0" style={{ transform: 'scaleX(-1)' }} />
                        <canvas ref={canvasRef} className="hidden" />
                      </div>
                      <Button type="button" onClick={capturePhoto} className="w-full mt-2 bg-[#138808]" data-testid="capture-photo-btn"><Camera className="w-4 h-4 mr-1" />Capture</Button>
                    </>
                  )}
                  {formData.photo_base64 && !cameraStream && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-16 h-16 rounded-lg overflow-hidden"><img src={`data:image/jpeg;base64,${formData.photo_base64}`} alt="" className="w-full h-full object-cover" /></div>
                      <div><p className="text-sm font-medium text-green-800">Photo captured!</p><Button type="button" variant="link" size="sm" onClick={startCamera} className="text-green-600 p-0 h-auto">Retake</Button></div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="save-employee-btn">{selectedEmployee ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Employee</DialogTitle><DialogDescription>Delete {selectedEmployee?.name}? This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
